import { PricingTier } from "@/types/pricingTier";
import { Quantities } from "@/types/quantities";
import { getTotalQuantity } from "./utils";
import { Price } from "@/types/price";
import { getNetFromGross } from "./utils";
import { Product } from "@/types/product";

const getMinProductQuantity = (quantities: Quantities, products: Product[]) => {
    let minQuantity = 0;

    for (const key in quantities) {
        if (isPosChecker(key, products)) continue;

        const quantity = quantities[key];
        if (minQuantity === 0 || (quantity > 0 && quantity < minQuantity)) {
            minQuantity = quantity;
        }
    }

    return minQuantity;
};

export function getDefaultPricingTier(
    pricingTiers: PricingTier[],
    quantities: Quantities,
    products: Product[]
): PricingTier {
    const minProductQuantity = getMinProductQuantity(quantities, products);

    const totalQuantity = getTotalQuantity(
        Object.fromEntries(
            Object.entries(quantities).filter(
                ([sku]) => !isPosChecker(sku, products)
            )
        )
    );

    const pricingTier = pricingTiers.find((pricingTier) => {
        return (
            pricingTier.minQuantityPerProduct <= minProductQuantity &&
            totalQuantity >= pricingTier.minQuantity
        );
    });

    return pricingTier ?? pricingTiers[pricingTiers.length - 1];
}

export function getPricingTier(
    pricingTiers: PricingTier[],
    tierName: string
): PricingTier {
    return pricingTiers.find((pricingTier) => pricingTier.name === tierName);
}

export function getPriceBeforeDiscount(
    price: Price,
    taxRate: number,
    netPricing: boolean
): number {
    let totalPrice = price.extraCost + price.value;

    if (netPricing) {
        totalPrice = getNetFromGross(totalPrice, taxRate);
    }

    return Math.round((totalPrice + Number.EPSILON) * 100) / 100;
}

export function getNetPriceAfterDiscount(
    price: Price,
    discount: number,
    taxRate: number
): number {
    if (!price?.value || price.extraCost === null) {
        throw new Error(
            "Invalid price object or no extraCost: " + JSON.stringify(price)
        );
    }

    const discountedPrice =
        getNetFromGross(price.extraCost, taxRate) +
        ((100 - discount) * price.value) / 100;

    return Math.round((discountedPrice + Number.EPSILON) * 100) / 100;
}

export function getDiscountForFixedNetPrice(
    rrpPrice: Price,
    fixedNetPrice: Price
): number {
    if (!rrpPrice?.value || !fixedNetPrice?.value) {
        throw new Error(
            "Invalid rrPrice or fixedNetPrice: " +
                JSON.stringify(rrpPrice) +
                " " +
                JSON.stringify(fixedNetPrice)
        );
    }

    const discount =
        ((rrpPrice.value - fixedNetPrice.value) / rrpPrice.value) * 100;
    return Math.round(discount + Number.EPSILON);
}

const isPosChecker = (sku: string, products: Product[]): boolean => {
    const product = products.find((p) => p.sku === sku);
    return product ? product.isPos : false;
};
